import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Audience,
  ExperienceActor,
  ExperienceActorType,
  ExperienceAnchor,
  ExperienceAnchorType,
  ExperienceChannelActor,
  ExperienceStatus,
  getSubTaskTemplates,
  IAutomationDenormalized,
  IExperience,
  isEventDrivenTiming,
  IExperienceDenormalized,
  IExperienceTemplate,
  ISubTask,
  ISupportingActor,
  ITask,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  SubTaskStatus,
  SupportingActorType,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import {
  ExperiencePreview,
  ExperiencePreviewExperience,
  ExperiencePreviewInstance,
  ExperiencePreviewMode,
  IExperiencePreview,
  PreviewBatch,
  PreviewGroup,
  PreviewGroupType,
  PreviewItemType
} from '@dadohr/shared/domainTypes/experiencePreview';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { DefaultDadoField, IUser } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { compact, flatten, fromPairs, get, keyBy, orderBy } from 'lodash';
import moment from 'moment';
import shortid from 'shortid';
import { UnsafeTFunction } from '../../../../../i18next';
import { channelToKey } from '../../../../../services/channels';
import {
  filterBlocksForAudience,
  getAudienceForUser,
  getEveryoneAudience
} from '../../../../../services/experiences/audience';
import {
  generateAccessToken,
  isExperienceUserActor,
  toDenormalizedAutomations,
  toDenormalizedExperience
} from '../../../../../services/experiences/instance';
import {
  getAutomationTemplateById,
  getExpectedEndFromTemplate,
  getMessageTemplateById,
  getTaskTemplateById,
  getTriggerHierarchyByEffect,
  toAutomationInstances
} from '../../../../../services/experiences/template';
import {
  getAnchorOffsetForExperience,
  getApproximateParentTimingForTriggeredItem,
  getFixedTimingOrApproximateParentTiming,
  getFreshNonTaskStartDate,
  getFreshTaskDates,
  getPresumedStartPositionEvaluatingTriggerChains
} from '../../../../../services/experiences/timing';
import { toDenormalizedOrg } from '../../../../../services/orgs';
import {
  addToTs,
  compareTimestamps,
  fromMoment,
  getMomentInTz,
  now,
  toMoment,
  tsFromMillis
} from '../../../../../services/time';
import {
  EXAMPLE_USER,
  getUserValue,
  matchUser
} from '../../../../../services/user';
import { AnchorField, getAnchorFields } from '../../../components/Anchors';
import { MinimalTaskBuilderContext } from '../../../components/Builders/TaskBuilder/types';
import { PreviewOptions } from './types';

export const getExperiecneActorFromSupportingActorDefinition = (
  participant: Doc<IUser>,
  a: ISupportingActor,
  anchorDate: Timestamp
): ExperienceActor | null => {
  if (a.type === 'staticUser') {
    const uId =
      a.ruleBasedUserIds.find((r) => matchUser(participant, r.rule, anchorDate))
        ?.userId || a.defaultUserId;
    if (uId) {
      return {
        type: ExperienceActorType.USER,
        userId: uId,
        key: a.key,
        accessToken: generateAccessToken()
      };
    }
  }
  if (a.type === 'user' && a.source === 'PROFILE') {
    const otherUserId = get(participant.data as any, a.field);
    if (otherUserId && typeof otherUserId === 'string') {
      return {
        type: ExperienceActorType.USER,
        userId: otherUserId,
        key: a.key,
        accessToken: generateAccessToken()
      };
    }
  }

  if (a.type === SupportingActorType.CHANNEL) {
    const expChannelActor: ExperienceChannelActor = {
      type: ExperienceActorType.CHANNEL,
      key: a.key,
      accessToken: generateAccessToken() /// should we actually genrate one?
    };
    return expChannelActor;
  }
  if (!a.ignoreWhenMissing) {
    console.warn('missing supporting actor', {
      supportingActor: a,
      participant
    });
  }
  return null;
};

export const PREVIEW_PARTICIPANT_ID = 'PREVIEW_PARTICIPANT';

const cleanupPreviewInstanceRemoveEffectsForWhichThereAreNoTriggers = (
  tpl: Doc<IExperienceTemplate>,
  inst: Doc<IExperience>
): Doc<IExperience> => {
  // It's important to have these properly sorted, so that chains are going to get
  // evaluated correctly

  const triggerHierarchyByEffect = getTriggerHierarchyByEffect(tpl);
  const hasCompleteTriggerChain = (
    type: 'automations' | 'messages' | 'tasks',
    id: string,
    depth = 0
  ): boolean => {
    if (depth > 20) {
      return false;
    }
    const chain = triggerHierarchyByEffect[type][id];
    if (!chain) {
      return false;
    }
    // Potentially dangerous if there are illegal cycles defined in trigger chains
    // The depth parameter tries to bail us out here
    return chain.every((x) => {
      const taskTpl = getTaskTemplateById(tpl, x);
      if (taskTpl?.timing.type === 'triggered') {
        return hasCompleteTriggerChain('tasks', x, depth + 1);
      }
      return !!inst.data.tasks.find((t) => t.templateId === x);
    });
  };

  return {
    ...inst,
    data: {
      ...inst.data,
      tasks: inst.data.tasks.filter((t) => {
        const tplTask = getTaskTemplateById(tpl, t.templateId);

        if (!tplTask) {
          return false;
        }
        if (tplTask.timing.type !== 'triggered') {
          return true;
        }
        return hasCompleteTriggerChain('tasks', tplTask.id);
      }),
      automations: Object.fromEntries(
        Object.entries(inst.data.automations).filter(([id, a]) => {
          const aTpl = getAutomationTemplateById(tpl, a.templateId);
          if (!aTpl) {
            return false;
          }
          if (aTpl.timing.type !== 'triggered') {
            return true;
          }
          return hasCompleteTriggerChain('automations', aTpl.id);
        })
      ),
      messagesStatuses: Object.fromEntries(
        Object.entries(inst.data.messagesStatuses).filter(([id, m]) => {
          const mTpl = getMessageTemplateById(tpl, id);
          if (!mTpl) {
            return false;
          }
          if (mTpl.timing.type !== 'triggered') {
            return true;
          }
          return hasCompleteTriggerChain('messages', mTpl.id);
        })
      )
    }
  };
};

const createPreviewExperieneInstance = (
  tpl: Doc<IExperienceTemplate>,
  user: Doc<IUser>,
  actors: ExperienceActor[],
  startsAt: Timestamp,
  status: ExperienceStatus,
  opts: { ignoreAudience?: boolean } = {}
) => {
  const experienceId = shortid();

  const expectedEnd = getExpectedEndFromTemplate(tpl, startsAt);
  const anchorDate = addToTs(
    startsAt,
    getAnchorOffsetForExperience(tpl.data.anchor)
  );
  const userId = user.id;
  // const _actors = actors || [
  //   {
  //     key: PARTICIPANT_ACTOR_KEY,
  //     userId,
  //     accessToken
  //   },
  //   ...Object.values(tpl.data.supportingActors).map((a) => ({
  //     key: a.key,
  //     userId: `PREVIEW_${a.key.toUpperCase()}`,
  //     accessToken: generateAccessToken()
  //   }))
  // ];

  const createdAt = now();
  const launchAt =
    compareTimestamps(createdAt, startsAt) < 0 ? startsAt : createdAt;

  const data: IExperience = {
    id: experienceId,
    orgId: tpl.data.orgId,
    templateId: tpl.id,
    userIds: [userId],
    accessTokens: [],
    anchorOffsetOverride: null,
    anchorDate: {
      at: anchorDate
    },
    messageOverrides: null,
    actors,
    ignoredMissingActors: {},
    ignoredVariables: [],
    status,
    startsAt,
    expectedEnd,
    launchAt,
    createdAt,
    finishedAt: null,
    messagesStatuses: fromPairs(
      compact(
        tpl.data.messages.other.map((m) => {
          const audience = getAudienceForUser(m.audiences, user, startsAt);
          if (!audience.blocks.length && !opts.ignoreAudience) {
            return null;
          }
          return [
            m.id,
            {
              audience: audience.id,
              status: MessageStatus.UPCOMING,
              overrides: { at: null },
              timing: {
                at: getFreshNonTaskStartDate(
                  getFixedTimingOrApproximateParentTiming(
                    m.timing,
                    tpl.data.tasks
                  ),
                  anchorDate,
                  tpl.data.anchor,
                  tpl.data.tz
                ),
                manualOverride: false
              }
            }
          ];
        })
      )
    ),
    tasks: compact(
      tpl.data.tasks.map<ITask | null>((t) => {
        const audience = getAudienceForUser(t.audiences, user, startsAt);
        const subTasks = (opts.ignoreAudience
          ? getSubTaskTemplates(t)
          : filterBlocksForAudience(getSubTaskTemplates(t), audience)
        ).map<ISubTask>((s) => ({
          id: shortid(),
          parentLayoutElement: null,
          templateId: s.id,
          type: s.type,
          status: SubTaskStatus.OPEN,
          completedAt: null,
          requirementType: s.requirementType
        }));
        if (subTasks.length < 1) {
          return null;
        }

        return {
          id: shortid(),
          experienceId,
          templateId: t.id,
          status: TaskStatus.NOT_STARTED, // TODO Triggered Items - this distinction is not needed in this component I think t.start === 0 ? TaskStatus.RUNNING : TaskStatus.NOT_STARTED,
          channels: t.channels,
          actor: t.actor,
          overrides: null,
          subTasks,
          audience: audience.id,
          timing: {
            manualOverride: false,
            ...getFreshTaskDates(
              t.timing.type === 'triggered'
                ? getApproximateParentTimingForTriggeredItem(
                    t.timing,
                    tpl.data.tasks
                  )
                : t.timing.type === 'dataChange'
                ? {
                    type: 'fixed' as const,
                    start: t.timing.earliestStart ?? 0,
                    end: (t.timing.earliestStart ?? 0) + t.timing.end.duration,
                    time: t.timing.time
                  }
                : t.timing,
              anchorDate,
              tpl.data.anchor,
              tpl.data.tz
            )
          }
        };
      })
    ),
    instanceErrors: [],
    automations: toAutomationInstances(
      experienceId,
      tpl.data.automations,
      user.id === PREVIEW_PARTICIPANT_ID ? 'EVERYONE' : user,
      anchorDate,
      tpl.data.anchor,
      tpl.data.tz,
      tpl.data.tasks,
      startsAt,
      { ...opts, includeTriggeredItems: true }
    ),
    events: [],
    messages: [],
    lastEvent: null,
    v: 1
  };

  return {
    id: experienceId,
    collection: FS.experiences,
    data
  };
};
const STARTING_DAY_HOUR = 9;
const getInstanceDenormalizedExperiencesAndAutomation = (
  tpl: Doc<IExperienceTemplate>,
  org: IOrganization,
  previewOptions: PreviewOptions
): [
  Doc<IExperience>,
  Doc<IExperienceDenormalized>[],
  IAutomationDenormalized[]
] => {
  const { anchorDate } = previewOptions;
  const _startDate = anchorDate
    ? getExperienceStartDate(tpl, org.tz, anchorDate)
    : getExperienceStartDate(
        tpl,
        org.tz,
        calculateInitalAnchorDate(tpl, org.tz)
      );

  const previewParticipant = {
    id: PREVIEW_PARTICIPANT_ID,
    collection: FS.users,
    data: EXAMPLE_USER(org.id, org.customUserFields)
  };

  const participantUser =
    previewOptions.mode === ExperiencePreviewMode.INSTANCE
      ? previewOptions.participantUser
      : previewParticipant;

  if (
    tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
    anchorDate
  ) {
    const field = tpl.data.anchor.field;
    (participantUser.data as any)[field] = anchorDate;
  }

  const actors: { [actorKey: string]: Doc<IUser> } = {
    [PARTICIPANT_ACTOR_KEY]: participantUser
  };

  const _anchorDate =
    anchorDate ||
    addToTs(_startDate, getAnchorOffsetForExperience(tpl.data.anchor));

  Object.values(tpl.data.supportingActors).forEach((a) => {
    if (previewOptions.mode === ExperiencePreviewMode.INSTANCE) {
      const actor = getExperiecneActorFromSupportingActorDefinition(
        participantUser,
        a,
        _anchorDate
      );
      if (actor) {
        const actorUser =
          actor &&
          isExperienceUserActor(actor) &&
          previewOptions.usersInOrg.find((u) => u.id === actor.userId);
        if (actorUser) {
          actors[a.key] = actorUser;
          return;
        }
      }
    }
    // Channels don't need an actor defined here - they are handled separately later
    if (a.type !== SupportingActorType.CHANNEL) {
      actors[a.key] = {
        id: 'MISSING_ACTOR',
        collection: FS.users,
        data: EXAMPLE_USER(org.id, org.customUserFields)
      };
    }
  });

  const experienceActors: ExperienceActor[] = compact([
    // This includes all the non-channel actors
    ...Object.entries(actors).map(([key, actorUser]) => ({
      type: ExperienceActorType.USER,
      key: key,
      userId: actorUser.id,
      accessToken: ''
    })),
    ...Object.values(tpl.data.supportingActors).map((sa) => {
      if (sa.type === SupportingActorType.CHANNEL) {
        const expChannelActor: ExperienceChannelActor = {
          type: ExperienceActorType.CHANNEL,
          key: sa.key,
          accessToken: ''
        };
        return expChannelActor;
      } else {
        return null;
      }
    })
  ]);

  const _previewInstance: Doc<IExperience> = createPreviewExperieneInstance(
    tpl,
    participantUser,
    experienceActors,
    _startDate,
    ExperienceStatus.RUNNING,
    { ignoreAudience: previewOptions.mode === ExperiencePreviewMode.EXPERIENCE }
  );

  const previewInstance = cleanupPreviewInstanceRemoveEffectsForWhichThereAreNoTriggers(
    tpl,
    _previewInstance
  );

  const denomalizedExperiences = experienceActors.map((actor) =>
    toDenormalizedExperience(
      actor.key,
      previewInstance,
      tpl,
      { id: org.id, collection: FS.orgs, data: org },
      actors,
      {},
      {
        ignoreCurrentTime: true,
        includeSkippedItems: true,
        ignoreAudience: previewOptions.mode === ExperiencePreviewMode.EXPERIENCE
      }
    )
  );

  const denormalizedAutomations = toDenormalizedAutomations(
    previewInstance,
    tpl
  );

  return [previewInstance, denomalizedExperiences, denormalizedAutomations];
};

const getAutomationTime = (
  automation: IAutomationDenormalized,
  experience: Doc<IExperience>
): Timestamp => {
  if (automation.timing.type === 'ABSOLUTE') {
    return automation.timing.start;
  }
  const taskId = automation.timing.taskId;
  const associatedTask = experience.data.tasks.find((t) => t.id === taskId);
  if (!associatedTask) {
    throw Error('Unkonw associated task for automation');
  }
  return associatedTask.timing.start;
};

const getAnchorName = (
  t: UnsafeTFunction,
  anchor: ExperienceAnchor,
  anchorFields: AnchorField[]
): string | null => {
  if (anchor.type !== ExperienceAnchorType.EMPLOYEE_DATE) {
    return null;
  }
  const userField = anchor.field;

  const anchorUserField = anchorFields.find((f) => f.field === userField);
  const filedLable = anchorUserField?.shortLabel(t) || '';
  return filedLable;
};

export const getExperienceStartDate = (
  tpl: Doc<IExperienceTemplate>,
  tz: string,
  anchorDate: Timestamp
): Timestamp => {
  const { anchor } = tpl.data;

  if (anchor.type === ExperienceAnchorType.IMMEDIATE) {
    if (anchor.offset.type === 'ABSOLUTE') {
      return fromMoment(
        toMoment(anchorDate)
          .tz(tz)
          .add(anchor.offset.duration, 'ms')
          .startOf('h')
          .hour(anchor.offset.hour)
          .minutes(anchor.offset.minute || 0)
      );
    }
  }
  if (anchor.type === ExperienceAnchorType.FIXED) {
    return anchor.ts;
  }
  if (anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    if (anchor.offset.type === 'ABSOLUTE') {
      return fromMoment(
        toMoment(anchorDate)
          .tz(tz)
          .add(anchor.offset.duration, 'ms')
          .startOf('h')
          .hour(anchor.offset.hour)
          .minutes(anchor.offset.minute || 0)
      );
    }
  }
  return anchorDate;
};

export const calculateInitalAnchorDate = (
  tpl: Doc<IExperienceTemplate>,
  tz: string
) => {
  const today = moment().tz(tz).startOf('h').hour(STARTING_DAY_HOUR);
  const anchorDate = addToTs(
    fromMoment(today),
    getAnchorOffsetForExperience(tpl.data.anchor)
  );

  const anchorMoment = getMomentInTz(tz, anchorDate.toMillis());
  const nextMonday = anchorMoment
    .add(1, 'week')
    .startOf('isoWeek')
    .startOf('h')
    .hour(STARTING_DAY_HOUR);
  console.log('nextMonday', nextMonday.toISOString());
  return fromMoment(nextMonday);
};

const get9oClockInMillis = (ts: Timestamp, timezone: string) => {
  const asMoment = toMoment(ts).tz(timezone);
  const nineOClock = asMoment.startOf('h').hour(9);
  const inMillis = fromMoment(nineOClock).toMillis();
  return inMillis;
};

const channelsToKey = (channels: ScheduleChannel[]) =>
  [...channels].sort().map(channelToKey).join('_');

export const getMiniMalTaksBuilderContext = (
  experiencePreview: ExperiencePreview
): MinimalTaskBuilderContext => {
  return {
    title: experiencePreview.title,
    contacts: experiencePreview.contacts,
    supportingActors: experiencePreview.supportingActors,
    anchor: experiencePreview.anchor,
    tz: experiencePreview.org.tz,
    orgOverrides: {}
  };
};

const getAudiences = (
  previewOptions: PreviewOptions,
  audiences: Audience[]
) => {
  if (previewOptions.mode === ExperiencePreviewMode.INSTANCE) {
    const userAudience = getAudienceForUser(
      audiences,
      previewOptions.participantUser,
      now()
    );
    return [userAudience, getEveryoneAudience(audiences)];
  }
  return audiences;
};

export const getPreviewGroups = (
  tpl: Doc<IExperienceTemplate>,
  org: IOrganization,
  previewOptions: PreviewOptions,
  t: UnsafeTFunction
): ExperiencePreview => {
  const [
    previewInstance,
    denormalizedExperiences,
    denormalizedAutomations
  ] = getInstanceDenormalizedExperiencesAndAutomation(tpl, org, previewOptions);

  const denormalizedTasksByTplId = keyBy(
    flatten(denormalizedExperiences.map((x) => x.data.tasks)),
    (t) => t.templateId
  );

  const groups = new Map<string, Map<string, PreviewBatch>>();

  const startMillis = get9oClockInMillis(previewInstance.data.startsAt, org.tz);
  const anchorMillis = get9oClockInMillis(
    previewInstance.data.anchorDate.at,
    org.tz
  );

  const endMillis =
    get9oClockInMillis(previewInstance.data.expectedEnd, org.tz) +
    tpl.data.expectedEndThreshold;

  groups.set(startMillis.toString(), new Map<string, PreviewBatch>());
  groups.set(endMillis.toString(), new Map<string, PreviewBatch>());
  if (tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    groups.set(anchorMillis.toString(), new Map<string, PreviewBatch>());
  }

  const toGroupKey = (millis: number, triggerTplIds: string[] = []) => {
    return [millis.toString(), ...compact(triggerTplIds)].join('|');
  };

  denormalizedExperiences.forEach((denormalizedExperience) => {
    denormalizedExperience.data.messages.forEach((message) => {
      const nineOClockInMillis = get9oClockInMillis(message.sendAt, org.tz);
      const exactTimeInMillis = message.sendAt.toMillis();

      const messageTemplate = getMessageTemplateById(tpl, message.templateId);
      if (!messageTemplate) {
        return;
      }
      const triggered = isEventDrivenTiming(messageTemplate.timing)
        ? messageTemplate.timing.type === 'triggered'
          ? {
              by: messageTemplate.timing.dependentOn.map((x) => x.itemId)
            }
          : { by: [] }
        : null;
      const groupKey = toGroupKey(nineOClockInMillis, triggered?.by);
      if (!groups.has(groupKey)) {
        groups.set(groupKey, new Map<string, PreviewBatch>()); // init if necesary
      }
      const groupBatches = groups.get(groupKey)!; //we ensured initialization above this is fine
      const batchKey = `${exactTimeInMillis}_${message.actor}_${channelsToKey(
        messageTemplate.channels
      )}`;
      if (!groupBatches.has(batchKey)) {
        groupBatches.set(batchKey, {
          actorKey: message.actor,
          channels: messageTemplate.channels,
          ts: tsFromMillis(exactTimeInMillis),
          items: []
        });
      }
      const batch = groupBatches.get(batchKey)!;

      batch.items.push({
        type: PreviewItemType.MESSAGE,
        audiences: getAudiences(previewOptions, messageTemplate.audiences),
        item: message,
        triggered
      });
    });

    denormalizedExperience.data.tasks.forEach((task) => {
      const nineOClockInMillis = get9oClockInMillis(task.start, org.tz);
      const exactTimeInMillis = task.start.toMillis();
      const taskTemplate = getTaskTemplateById(tpl, task.templateId);
      if (!taskTemplate) {
        return;
      }
      const triggered = isEventDrivenTiming(taskTemplate.timing)
        ? taskTemplate.timing.type === 'triggered'
          ? {
              by: taskTemplate.timing.dependentOn.map((x) => x.itemId),
              deadline: taskTemplate.timing.end
            }
          : { by: [], deadline: taskTemplate.timing.end }
        : null;
      const groupKey = toGroupKey(nineOClockInMillis, triggered?.by);
      if (!groups.has(groupKey)) {
        groups.set(groupKey, new Map<string, PreviewBatch>()); // init if necesary
      }
      if (nineOClockInMillis === endMillis) {
        console.log('END TASK', task);
      }
      const groupBatches = groups.get(groupKey)!; //we ensured initialization above this is fine

      const batchKey = `${exactTimeInMillis}_${task.actor}_${channelsToKey(
        taskTemplate.channels
      )}`;
      if (!groupBatches.has(batchKey)) {
        groupBatches.set(batchKey, {
          actorKey: task.actor,
          ts: tsFromMillis(exactTimeInMillis),
          channels: taskTemplate.channels,
          items: []
        });
      }
      const batch = groupBatches.get(batchKey)!;

      batch.items.push({
        type: PreviewItemType.TASK,
        audiences: getAudiences(previewOptions, taskTemplate.audiences),
        item: task,

        triggered
      });
    });
  });

  denormalizedAutomations.forEach((automation) => {
    const ts = getAutomationTime(automation, previewInstance);
    const automationTpl = getAutomationTemplateById(tpl, automation.templateId);
    const nineOClockInMillis = get9oClockInMillis(ts, org.tz);
    const exactTimeInMillis = ts.toMillis();
    if (!automationTpl) {
      return;
    }

    const triggered = isEventDrivenTiming(automationTpl.timing)
      ? automationTpl.timing.type === 'triggered'
        ? {
            by: automationTpl.timing.dependentOn.map((x) => x.itemId)
          }
        : { by: [] }
      : null;
    const groupKey = toGroupKey(nineOClockInMillis, triggered?.by);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, new Map<string, PreviewBatch>()); // init if necesary
    }
    const groupBatches = groups.get(groupKey)!; //we ensured initialization above this is fine

    const actorKey = 'AUTOMATION';

    const batchKey = `${exactTimeInMillis}_${actorKey}`;

    if (!groupBatches.has(batchKey)) {
      groupBatches.set(batchKey, {
        actorKey: actorKey,
        channels: [],
        ts: tsFromMillis(exactTimeInMillis),
        items: []
      });
    }
    const batch = groupBatches.get(batchKey)!;

    batch.items.push({
      type: PreviewItemType.AUTOMATION,
      audiences: getAudiences(previewOptions, automationTpl.audiences),
      item: automation,
      triggered
    });
  });

  const getGroupType = (millis: number) => {
    switch (millis) {
      case startMillis:
        return PreviewGroupType.START;
      case endMillis:
        return PreviewGroupType.END;
      case anchorMillis:
        return PreviewGroupType.ANCHOR;
      default:
        return PreviewGroupType.NORMAL;
    }
  };

  const anchorMoment = toMoment(tsFromMillis(anchorMillis));
  const previewGroups = Array.from(
    groups.entries(),
    ([key, batchMap]): PreviewGroup => {
      const [millisString, ...triggerTaskTplIds] = key.split('|');
      const millis = parseInt(millisString);
      const ts = tsFromMillis(millis);
      const tsMoment = toMoment(ts);

      const intervalToAnchorInDays: number = tsMoment.diff(
        anchorMoment,
        'days'
      );
      return {
        type: triggerTaskTplIds.length
          ? PreviewGroupType.TRIGGERED
          : getGroupType(millis),
        batches: Array.from(batchMap.values()),
        ts,
        intervalToAnchorInDays,
        triggeredBy: compact(
          triggerTaskTplIds.map((id) => denormalizedTasksByTplId[id] || null)
        )
      };
    }
  );

  const sortedGroups = orderBy(previewGroups, [
    (g) => g.ts.toMillis(),
    // no triggers go first
    (g) => (g.triggeredBy || []).length,
    // and chains of triggered items need to sorted correctly
    (g) => {
      if (!g.triggeredBy?.length) {
        return 0;
      }
      return getPresumedStartPositionEvaluatingTriggerChains(
        g.triggeredBy.map((x) => x.templateId),
        tpl.data.tasks
      );
    }
  ]);
  const anchorFields = getAnchorFields(org.customUserFields);
  const anchorName = getAnchorName(t, tpl.data.anchor, anchorFields);

  const contacts = tpl.data.contacts;

  const preview: IExperiencePreview = {
    groups: sortedGroups,
    anchor: tpl.data.anchor,
    title: tpl.data.title,
    contacts,
    supportingActors: tpl.data.supportingActors,
    anchorName,
    org: toDenormalizedOrg(org, tpl.data.overrides?.org || null),
    customFields: org.customUserFields,
    tz: tpl.data.tz
  };
  const missingActors = previewInstance.data.actors
    .filter(isExperienceUserActor)
    .filter((a) => a.userId === 'MISSING_ACTOR')
    .map((a) => a.key);

  const modePart: ExperiencePreviewExperience | ExperiencePreviewInstance =
    previewOptions.mode === ExperiencePreviewMode.INSTANCE
      ? {
          mode: ExperiencePreviewMode.INSTANCE,
          missingActors,
          id: previewOptions.participantUser.id,
          participantName: getUserValue(
            previewOptions.participantUser.data,
            'preferredName',
            now()
          ),
          participantFullName: `${getUserValue(
            previewOptions.participantUser.data,
            DefaultDadoField.PREFERRED_NAME,
            now()
          )} ${getUserValue(
            previewOptions.participantUser.data,
            DefaultDadoField.LAST_NAME,
            now()
          )}`
        }
      : { mode: ExperiencePreviewMode.EXPERIENCE };

  return {
    ...preview,
    ...modePart
  };
};
